import { ConditionType } from "types/condition";
const ALL_VARIBLE_TYPES = ["BOOLEAN", "DATE", "NUMERIC", "NULL", "TEXT"];
export var DatePart;
(function (DatePart) {
    DatePart["YEAR"] = "year";
    DatePart["MONTH"] = "month";
    DatePart["DAY"] = "day";
    DatePart["DECADE"] = "decade";
    DatePart["CENTURY"] = "century";
    DatePart["MILLENNIUM"] = "millennium";
    DatePart["MICROSECONDS"] = "microseconds";
    DatePart["MILLISECONDS"] = "milliseconds";
    DatePart["SECOND"] = "second";
    DatePart["MINUTE"] = "minute";
    DatePart["HOUR"] = "hour";
    DatePart["EPOCH"] = "epoch";
    //DOW = "dow",
    DatePart["ISODOW"] = "isodow";
    DatePart["WEEK"] = "week";
    DatePart["YEARWEEK"] = "yearweek";
    DatePart["DAYOFYEAR"] = "dayofyear";
    DatePart["QUARTER"] = "quarter";
})(DatePart || (DatePart = {}));
const FUNCTION_INPUTS = {
    first: ALL_VARIBLE_TYPES,
    last: ALL_VARIBLE_TYPES,
    count: ["NUMERIC"],
    sum: ["NUMERIC"],
    mean: ["NUMERIC"],
    min: ["NUMERIC"],
    max: ["NUMERIC"],
    abs: ["NUMERIC"],
    ln: ["NUMERIC"],
    log: ["NUMERIC"],
    log2: ["NUMERIC"],
    sqrt: ["NUMERIC"],
    trim: ["TEXT"],
    ltrim: ["TEXT"],
    rtrim: ["TEXT"],
    parse_date: ["TEXT"],
    lower: ["TEXT"],
    upper: ["TEXT"]
};
export function getFunctionInputs(func) {
    return FUNCTION_INPUTS[func];
}
export function getFunctionArguments(func) {
    if (func === "parse_date") {
        return [
            {
                arg: "format_str",
                label: "Format String",
                type: "TEXT"
            }
        ];
    }
    return undefined;
}
const OPERATOR_INPUTS = {
    "+": ["NUMERIC"],
    "-": ["NUMERIC"],
    "*": ["NUMERIC"],
    "/": ["NUMERIC"]
};
export function getBinaryOperatorInputs(op) {
    return OPERATOR_INPUTS[op];
}
export function isScalar(expr) {
    return (expr === undefined ||
        expr === null ||
        typeof expr === "string" ||
        typeof expr === "boolean" ||
        typeof expr === "number");
}
export function stringifyScalar(expr) {
    if (expr === undefined || expr === null) {
        return "NULL";
    }
    else if (typeof expr === "boolean") {
        return expr ? "True" : "False";
    }
    else if (typeof expr === "string") {
        return expr;
    }
    else {
        return String(expr);
    }
}
export function isNonScalarColumn(expr) {
    return !("type" in expr) && "column" in expr;
}
export function isColumn(expr) {
    return !isScalar(expr) && isNonScalarColumn(expr);
}
export function isColumnProjection(expr) {
    return typeof expr === "string" || (!("type" in expr) && "column" in expr);
}
export function isBaseProjection(expr) {
    if (isColumnProjection(expr)) {
        return true;
    }
    if (expr.type !== "EXPRESSION" || isScalar(expr.expr)) {
        return false;
    }
    return !isColumn(expr.expr) && expr.expr.type === "CAST" && isColumn(expr.expr.expr);
}
export function getBaseCurrentAlias(expr) {
    if (isColumnProjection(expr)) {
        if (typeof expr === "string") {
            return expr;
        }
        return expr.column;
    }
    return expr.expr.expr.column;
}
export function isAliasedProjection(expr) {
    return typeof expr === "string" || ("type" in expr && expr.type == "EXPRESSION") || "alias" in expr;
}
export function customValidationError(message) {
    return {
        error_message: message
    };
}
export function humanizeError(error) {
    console.log("Validation Error");
    console.log(error);
    if ("available_columns" in error) {
        return `The column '${error.column}' is not found the data. Please check your data transformations.`;
    }
    else if ("column" in error) {
        return `Check usage of column: '${error.column}'. This column was referenced in a context that was incompatible with it's type. Type: ${error.observed_type} Allowed Type(s): ${JSON.stringify(error.allowed_types)}`;
    }
    else if ("error_message" in error) {
        return error.error_message;
    }
    else {
        return `Expression returned invalid type. Output Type: ${error.output_type} Allowed Output Type(s): ${JSON.stringify(error.allowed_output_types)}`;
    }
}
export function binaryOperation(operator, exprs) {
    return {
        type: "BINARY_OPERATION",
        operator: operator,
        exprs: exprs
    };
}
export function column(column) {
    return {
        column: column
    };
}
export function caseCondition(condition, value) {
    return {
        condition: condition,
        value: value
    };
}
export function ifThen(cases, else_value) {
    return {
        type: "IF_THEN",
        cases: cases,
        else_value: else_value
    };
}
export function ifThenWithAlias(cases, else_value, alias) {
    return {
        type: "IF_THEN",
        cases: cases,
        else_value: else_value,
        alias: alias
    };
}
export function cast(expr, new_type) {
    return {
        type: "CAST",
        expr: expr,
        new_type: new_type
    };
}
export function datePart(date, part) {
    return {
        type: "DATE_PART",
        date: date,
        part: part
    };
}
export function scalarFunction(func, expr, args) {
    return columnExpr(func, expr, args);
}
export function unaryFunction(func, expr, args) {
    return columnExpr(func, expr, args);
}
export function columnExpr(func, expr, args) {
    return {
        type: "FUNCTION",
        func: func,
        expr: expr,
        args: args
    };
}
export function literal(expr) {
    return expr;
}
export function aliasedExpression(expr, alias) {
    return {
        type: "EXPRESSION",
        expr: expr,
        alias: alias
    };
}
export function aggregateMeasureExpression(expr, filter) {
    return {
        type: "AGGREGATE",
        expr: expr,
        filter: filter
    };
}
export function aggregateMeasure(expr, alias, filter) {
    return {
        type: "AGGREGATE",
        expr: expr,
        filter: filter,
        alias: alias
    };
}
export function columnProjection(column, alias) {
    return {
        column: column,
        alias: alias
    };
}
export function comparisonCondition(column, value, type = ConditionType.Equal) {
    return {
        type: type,
        column: column,
        value: value
    };
}
export function projectionToExpression(projection) {
    if (typeof projection === "string") {
        return column(projection);
    }
    if ("type" in projection && projection.type == "EXPRESSION") {
        return projection.expr;
    }
    return projection;
}
